import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "Store/configureStore";

import type {
  ConfigurationWizardEntry,
  ConfigurationWizardState,
  Topology
} from "./types";

const DEFAULT_ENTRY: ConfigurationWizardEntry = {
  phase: "consent"
};

// View model consumed by the component. Mirrors the state's discriminated union
// but exposes the result phase's derived, render-ready data (apps, configYaml)
// instead of the raw config.
type WizardView =
  | { phase: "consent" }
  | { phase: "loading"; loadingMessage?: string }
  | { phase: "result"; apps: string[]; configYaml: string; generatedAt: string }
  | { phase: "error"; error: string };

const extractApps = (topology: Topology): string[] => {
  const entries = [
    ...Object.values(topology.webapps),
    ...Object.values(topology.workers),
    ...Object.values(topology.services)
  ];
  return [
    ...new Set(entries.map(entry => entry.type?.split(":")[0]).filter(Boolean))
  ];
};

export const configurationWizardStateSelector = (
  state: RootState
): ConfigurationWizardState => state.configurationWizard;

export const configurationWizardDataSelector = createSelector(
  configurationWizardStateSelector,
  (_: RootState, params: { projectId: string; environmentId: string }) =>
    params.projectId,
  (_: RootState, params: { projectId: string; environmentId: string }) =>
    params.environmentId,
  (state, projectId, environmentId) => state.data?.[projectId]?.[environmentId]
);

export const configWizardSelector = createSelector(
  configurationWizardDataSelector,
  (entry): WizardView => {
    const data = entry ?? DEFAULT_ENTRY;

    switch (data.phase) {
      case "consent":
        return { phase: "consent" };
      case "loading":
        return { phase: "loading", loadingMessage: data.loadingMessage };
      case "result":
        return {
          phase: "result",
          apps: extractApps(data.config.topology),
          configYaml: data.config.configYaml,
          generatedAt: data.generatedAt
        };
      case "error":
        return { phase: "error", error: data.error };
    }
  }
);
